/**
 * ScopeContext - navigation scope and view state for the UI
 *
 * Holds per-client navigation state and provides scoped access
 * to both instance state (values) and definition state (nodes, connections).
 *
 * Scope path is synced with URL:
 * - Saved projects: /:sessionId/project/:projectName/Label1/Label2/...
 * - New drafts: /:sessionId/new/:draftId/Label1/Label2/...
 * Labels are resolved to nodeIds when reading URL, and nodeIds are converted to labels when writing URL.
 *
 * Definition-edit mode:
 * - URL contains ~def/DefinitionId — navigates directly into a definition
 * - A phantom runtime instance is created so the definition is live-evaluable
 * - The phantom instance is disposed when navigating away
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InstanceState, DefinitionState } from '@/runtime/RuntimeStore';
import { useRuntime } from './RuntimeProvider';

/**
 * Convert URL labels to nodeIds by walking the path.
 * Each segment is looked up in its parent's definition.
 * Returns the deepest valid path as nodeIds.
 */
function resolveUrlPathToNodeIds(
  urlLabels: string[],
  allInstanceState: Record<string, InstanceState>,
  allDefinitionState: Record<string, DefinitionState>
): string[] {
  const result: string[] = ['root'];

  for (let i = 1; i < urlLabels.length; i++) {
    const parentInstanceId = result.join('/');
    const parentInstance = allInstanceState[parentInstanceId];
    if (!parentInstance) break;

    const parentDefinition = allDefinitionState[parentInstance.definitionId];
    if (!parentDefinition) break;

    const label = urlLabels[i];
    // Find nodeId by label (or use as nodeId if no match)
    const nodeId = Object.entries(parentDefinition.nodes).find(
      ([id, node]) => (node.label || id) === label
    )?.[0] ?? label;

    // Check if this path exists
    const testPath = [...result, nodeId];
    if (!allInstanceState[testPath.join('/')]) break;

    result.push(nodeId);
  }

  return result;
}

/**
 * Convert nodeId path to URL labels.
 */
function nodeIdPathToLabels(
  nodeIdPath: string[],
  allInstanceState: Record<string, InstanceState>,
  allDefinitionState: Record<string, DefinitionState>
): string[] {
  const result: string[] = [];

  for (let i = 1; i < nodeIdPath.length; i++) {
    const parentInstanceId = nodeIdPath.slice(0, i).join('/');
    const parentInstance = allInstanceState[parentInstanceId];
    if (!parentInstance) {
      result.push(nodeIdPath[i]);
      continue;
    }

    const parentDefinition = allDefinitionState[parentInstance.definitionId];
    if (!parentDefinition) {
      result.push(nodeIdPath[i]);
      continue;
    }

    const nodeId = nodeIdPath[i];
    const node = parentDefinition.nodes[nodeId];
    result.push(node?.label || nodeId);
  }

  return result;
}

interface IScopeContext {
  // State
  instanceState: InstanceState | undefined;
  definitionState: DefinitionState | undefined;
  definitionId: string;
  // Navigation
  path: string[];
  pathLabels: string[];  // Human-readable labels for breadcrumbs
  setPath: (path: string[]) => void;
  navigateUp: () => void;
  canNavigateUp: boolean;
  instanceId: string;
  selectedNodeIds: string[];
  setSelectedNodeIds: (nodeIds: string[]) => void;
  clearSelection: () => void;
  setInstance: (nodeId: string) => void;
  // Definition-edit mode
  isDefinitionEdit: boolean;
  openDefinition: (definitionId: string) => void;
}

const ScopeContext = createContext<IScopeContext | null>(null);

const DEF_PREFIX = '~def';

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { sessionId, projectName, draftId, '*': scopePath } = useParams();
  const navigate = useNavigate();
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);

  const { instanceState: allInstanceState, definitionState: allDefinitionState, createPhantomInstance, disposePhantomInstance } = useRuntime();
  const clearSelection = useCallback(() => setSelectedNodeIds([]), []);

  // Derive requested path from URL labels
  const urlLabels = ['root', ...(scopePath ? scopePath.split('/').filter(Boolean) : [])];

  // Check for definition-edit mode: URL contains ~def/DefinitionId
  const defEditIndex = urlLabels.indexOf(DEF_PREFIX);
  const isDefinitionEdit = defEditIndex !== -1 && defEditIndex + 1 < urlLabels.length;
  const defEditId = isDefinitionEdit ? urlLabels[defEditIndex + 1] : null;

  // Manage phantom instance lifecycle
  const activePhantomRef = useRef<string | null>(null);

  // Only available once the definition has loaded (may be delayed on fresh page load)
  const defAvailable = isDefinitionEdit && defEditId && !!allDefinitionState[defEditId];

  useEffect(() => {
    if (defAvailable && defEditId) {
      createPhantomInstance(defEditId);
      activePhantomRef.current = defEditId;
    }

    return () => {
      if (activePhantomRef.current) {
        disposePhantomInstance(activePhantomRef.current);
        activePhantomRef.current = null;
      }
    };
  }, [defAvailable, defEditId, createPhantomInstance, disposePhantomInstance]);

  // Build URL base
  const urlBase = draftId
    ? `/${sessionId}/new/${draftId}`
    : `/${sessionId}/project/${encodeURIComponent(projectName!)}`;

  // Update URL when path changes (converts nodeIds to labels)
  const setPath = useCallback((newPath: string[]) => {
    // Definition-edit paths use raw segments (no label resolution needed)
    if (newPath[0] === DEF_PREFIX) {
      const scopePart = newPath.join('/');
      navigate(`${urlBase}/${scopePart}`, { replace: true });
      return;
    }

    // Convert nodeIds to labels for URL (skip 'root')
    const labels = nodeIdPathToLabels(newPath, allInstanceState, allDefinitionState);
    const scopePart = labels.join('/');
    const newUrl = scopePart ? `${urlBase}/${scopePart}` : urlBase;
    navigate(newUrl, { replace: true });
  }, [urlBase, navigate, allInstanceState, allDefinitionState]);

  // Navigate into a definition directly
  const openDefinition = useCallback((definitionId: string) => {
    setPath([DEF_PREFIX, definitionId]);
  }, [setPath]);

  let instanceId: string;
  let instanceState: InstanceState | undefined;
  let definitionId: string;
  let path: string[];
  let pathLabels: string[];

  // In definition-edit mode, check if the definition is actually loaded yet.
  // On a fresh page load the server sync may not have delivered it yet.
  const defEditReady = isDefinitionEdit && defEditId && allDefinitionState[defEditId];

  if (defEditReady && defEditId) {
    // Definition-edit mode: use phantom instance
    const phantomId = `~def/${defEditId}`;
    instanceId = phantomId;
    instanceState = allInstanceState[phantomId];
    definitionId = defEditId;
    path = [DEF_PREFIX, defEditId];
    pathLabels = [DEF_PREFIX, defEditId];
  } else {
    // Normal instance-based navigation
    path = resolveUrlPathToNodeIds(urlLabels, allInstanceState, allDefinitionState);
    instanceId = path.join('/');
    instanceState = allInstanceState[instanceId];

    if (!instanceState) {
      throw new Error(`ScopeContext: no instanceState for '${instanceId}'. Available: ${Object.keys(allInstanceState).join(', ')}`);
    }
    if (!instanceState.definitionId) {
      throw new Error(`ScopeContext: instanceState['${instanceId}'] has no definitionId`);
    }
    definitionId = instanceState.definitionId;
    pathLabels = ['root', ...nodeIdPathToLabels(path, allInstanceState, allDefinitionState)];
  }

  const navigateUp = useCallback(() => {
    if (defEditReady) {
      // Return to root from definition-edit mode
      setPath(['root']);
    } else {
      setPath(path.slice(0, -1));
    }
  }, [defEditReady, path, setPath]);

  const canNavigateUp = !!defEditReady || path.length > 1;

  // Navigate into a subgraph instance
  const setInstance = useCallback((nodeId: string) => {
    setPath([...path, nodeId]);
  }, [path, setPath]);

  const scopedDefinitionState = allDefinitionState[definitionId];
  if (!scopedDefinitionState) {
    throw new Error(`ScopeContext: no definitionState for '${definitionId}'. Available: ${Object.keys(allDefinitionState).join(', ')}`);
  }

  const value: IScopeContext = {
    instanceState,
    definitionState: scopedDefinitionState,
    definitionId,
    path,
    pathLabels,
    setPath,
    navigateUp,
    canNavigateUp,
    instanceId,
    setInstance,
    selectedNodeIds,
    setSelectedNodeIds,
    clearSelection,
    isDefinitionEdit: !!defEditReady,
    openDefinition
  };

  return (
    <ScopeContext.Provider value={value}>
      {children}
    </ScopeContext.Provider>
  );
}

export function useScope(): IScopeContext {
  const context = useContext(ScopeContext);
  if (!context) {
    throw new Error('useScope must be used within ScopeProvider');
  }
  return context;
}

/** Safe version that returns null when outside ScopeProvider (e.g. performance view inspector) */
export function useScopeOptional(): IScopeContext | null {
  return useContext(ScopeContext);
}
